/* Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial */
import type {
  LocalParticipant,
  LocalTrack,
  ScreenShareCaptureOptions,
} from "livekit-client";

/** Keep the system picker outside LiveKit's pending-publication lock. Some
 * mobile picker dismissals leave capture unresolved: the next explicit click
 * must be able to request capture again, with no stale request publishing later. */
export function createScreenShareController(
  participant: () => LocalParticipant | null,
  onError: (error: unknown) => void,
) {
  let generation = 0;
  let disposed = false;
  const publishing = new Set<LocalTrack[]>();
  const invalidate = (): void => {
    generation++;
    for (const tracks of publishing) for (const track of tracks) track.stop();
  };
  const release = async (
    owner: LocalParticipant,
    tracks: LocalTrack[],
  ): Promise<void> => {
    for (const track of tracks) track.stop();
    await Promise.allSettled(
      tracks.map(async (track) => {
        if (owner.getTrackPublication(track.source)?.track === track)
          await owner.unpublishTrack(track);
      }),
    );
  };
  return {
    async toggle(options: ScreenShareCaptureOptions): Promise<void> {
      if (disposed) return;
      const owner = participant();
      if (!owner) return;
      const stopSharing = owner.isScreenShareEnabled;
      invalidate();
      const attempt = generation;
      const current = (): boolean =>
        !disposed && generation === attempt && participant() === owner;
      let tracks: LocalTrack[] = [];
      try {
        if (stopSharing) {
          await owner.setScreenShareEnabled(false);
          return;
        }
        // Invoke directly in the click's activation, before any await. Unlike
        // setScreenShareEnabled(true), capture here does not lock publication.
        tracks = await owner.createScreenTracks(options);
        if (!current()) {
          await release(owner, tracks);
          return;
        }
        publishing.add(tracks);
        const results = await Promise.allSettled(
          tracks.map(async track => await owner.publishTrack(track)),
        );
        const failed = results.find(result => result.status === "rejected");
        if (failed?.status === "rejected") throw failed.reason;
        if (!current()) await release(owner, tracks);
      } catch (error) {
        await release(owner, tracks);
        if (current()) onError(error);
      } finally {
        publishing.delete(tracks);
      }
    },
    dispose(): void {
      disposed = true;
      invalidate();
    },
  };
}
